import CircleSpinner from 'components/circle-spinner/CircleSpinner'
import { CustomerForm } from 'constants/enums/formTypes'
import {
	ApplicationDispatchContext,
	LOAN_APPLICATION_ACTIONS,
} from 'contexts/ApplicationContext'
import { FormFactory } from 'formBuilder/formFactory'
import { formKeySelector, resetFormControlsStateAction } from 'formBuilder/src'
import FormBuilder from 'formBuilder/src/components/FormBuilder'
import { IApplicationContext } from 'interfaces/i-application-context-props'
import { IApplicationInformation } from 'interfaces/i-application-information'
import React, { useContext, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { getLanguageLocale, getMarket } from 'utils/countryHelpers'
import {
	addLeadingZeroToDates,
	formatDateToIsoDate,
	formatFromToIsoDate,
	isValidDate,
} from 'utils/dateAndTimeHelpers'

type FillPersonalDataUKProps = {
	locked: boolean
}

export const FillPersonalDataUK = ({ locked }: FillPersonalDataUKProps) => {
	const { t } = useTranslation()
	const { loanApplication, dispatchLoanApplication }: IApplicationContext =
		useContext(ApplicationDispatchContext)

	let applicationDetailsFormSchema = FormFactory(
		CustomerForm.ApplicationUpdateForm,
		{
			formName: 'applicationUpdateForm',
			blockName: 'applicationUpdateForm',
			market: getMarket(),
			locale: getLanguageLocale(),
			getDataUrl: '',
			validationErrorList: false,
			requiredMessage: t('ValidateRequired'),
		},
		loanApplication.applicationData as IApplicationInformation,
		// sanitizeApplicationData(
		// 	loanApplication.applicationData as IApplicationInformation,
		// ),
	)
	const applicationDetailsFormStateValues = useSelector(
		formKeySelector(
			applicationDetailsFormSchema.formMeta.formName,
			'controlsState',
		),
	)
	const dispatch = useDispatch()
	const wasLockedRef = useRef(locked)

	// discard unsaved edits and restore original values when edit mode is cancelled
	useEffect(() => {
		if (locked && !wasLockedRef.current) {
			dispatch(resetFormControlsStateAction(applicationDetailsFormSchema))
		}
		wasLockedRef.current = locked
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [locked])

	// whenever any field is updated in the forms, dispatch the updateApplicationData on the loanApplication
	useEffect(() => {
		console.log(
			'birthdate:' + applicationDetailsFormStateValues?.birthDate.value,
		)
		//TODO: Remove hardcoded values once we receive the data from PX
		dispatchLoanApplication({
			type: LOAN_APPLICATION_ACTIONS.SET_UPDATE_APPLICATIONDATA,
			payload: {
				...loanApplication,
				updateApplicationData: {
					...loanApplication.updateApplicationData,
					applicant: {
						...loanApplication.updateApplicationData?.applicant,
						personalInfo: {
							...loanApplication.updateApplicationData?.applicant
								?.personalInfo,
							firstName:
								applicationDetailsFormStateValues?.firstName
									.value,
							lastName:
								applicationDetailsFormStateValues?.lastName
									.value,
							birthDate: applicationDetailsFormStateValues
								?.birthDate.value
								? isValidDate(
										applicationDetailsFormStateValues
											.birthDate.value,
									)
									? formatDateToIsoDate(
											applicationDetailsFormStateValues
												.birthDate.value,
										)
									: applicationDetailsFormStateValues
											.birthDate.value
								: '',
							cityOfBirth:
								loanApplication.applicationData?.personalInfo
									?.cityOfBirth ?? 'London',
							nationality:
								loanApplication.applicationData?.personalInfo
									?.nationality,
						},

						contacts: {
							...loanApplication.updateApplicationData?.applicant
								?.contacts,
							email: applicationDetailsFormStateValues?.email
								.value,
						},
						employment: {
							...loanApplication.updateApplicationData?.applicant
								?.employment,
							occupationType:
								applicationDetailsFormStateValues
									?.occupationType.value,
							employedSince:
								loanApplication.applicationData?.employment
									?.employedSince,
							income: applicationDetailsFormStateValues
								?.monthlyNetIncome.value,
							otherIncome:
								applicationDetailsFormStateValues?.otherIncome
									.value,
						},
						address: {
							...loanApplication.updateApplicationData?.applicant
								?.address,
							streetNumber:
								applicationDetailsFormStateValues?.streetNumber
									.value,
							street: applicationDetailsFormStateValues?.street
								.value,
							flatNumber:
								applicationDetailsFormStateValues?.flatNumber
									.value,
							buildingName:
								applicationDetailsFormStateValues?.buildingName
									.value,
							zip: applicationDetailsFormStateValues?.zip.value,
							city: applicationDetailsFormStateValues?.city.value,
							timeAtAddress: applicationDetailsFormStateValues
								?.timeAtAddress.value
								? isValidDate(
										applicationDetailsFormStateValues
											.timeAtAddress.value,
									)
									? formatDateToIsoDate(
											applicationDetailsFormStateValues
												.timeAtAddress.value,
										)
									: formatFromToIsoDate(
											addLeadingZeroToDates(
												applicationDetailsFormStateValues
													.timeAtAddress.value,
											),
											'dd/MM/yyyy',
										)
								: '',
							county:
								loanApplication.applicationData?.address
									?.county ?? 'Test',
						},
						formerAddress: {
							...loanApplication.updateApplicationData?.applicant
								?.formerAddress,
							streetNumber:
								applicationDetailsFormStateValues
									?.formerStreetNumber.value,
							street: applicationDetailsFormStateValues
								?.formerStreet.value,
							flatNumber:
								applicationDetailsFormStateValues
									?.formerFlatNumber.value,
							buildingName:
								applicationDetailsFormStateValues
									?.formerBuildingName.value,
							zip: applicationDetailsFormStateValues?.formerZip
								.value,
							city: applicationDetailsFormStateValues?.formerCity
								.value,
							timeAtAddress: applicationDetailsFormStateValues
								?.formerTimeAtAddress.value
								? isValidDate(
										applicationDetailsFormStateValues
											.formerTimeAtAddress.value,
									)
									? formatDateToIsoDate(
											applicationDetailsFormStateValues
												.formerTimeAtAddress.value,
										)
									: formatFromToIsoDate(
											addLeadingZeroToDates(
												applicationDetailsFormStateValues
													.formerTimeAtAddress.value,
											),
											'dd/MM/yyyy',
										)
								: '',
						},
						accommodation: {
							...loanApplication.updateApplicationData?.applicant
								?.accommodation,
							type: applicationDetailsFormStateValues
								?.accomodationType.value,
							cost: applicationDetailsFormStateValues?.cost.value,
							childrenInFamily:
								applicationDetailsFormStateValues
									?.childrenInFamily.value,
						},
					},
					card: {
						...loanApplication.updateApplicationData?.card,
						immediateCashWithdrawal:
							applicationDetailsFormStateValues?.cashWithdrawal
								.value,
					},
				},
			},
		})
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [applicationDetailsFormStateValues])

	return (
		<div className="update-personal-data">
			<div className="update-personal-data-section">
				{applicationDetailsFormSchema ? (
					<FormBuilder
						formSchema={applicationDetailsFormSchema}
						apiError={undefined}
						showErrorsInForm={true}
						showSubmitButton={false}
						fieldLockMode={false}
						formLockMode={true}
						formLocked={locked}
					/>
				) : (
					<CircleSpinner />
				)}
			</div>
		</div>
	)
}
